/**
 * Unified task-card model shared by the repairs / maintenance / task-management
 * stores and their scheduling/assignment dialogs.
 *
 * Historically these types lived in the (since removed) kanban-board component;
 * they were extracted here because every consumer only needed the data shapes,
 * not the board UI.
 */

import { ServiceProvider } from './service-provider';
import type { InterventionPhase } from './intervention-phase';

export interface TaskCard {
  id: string;
  title: string;
  description: string;
  type: 'repair' | 'maintenance';
  status: TaskStatus;
  priority: 'low' | 'medium' | 'high' | 'emergency';

  /** Precomputed intervention phase (repairs only) — the list mini-bar source. */
  phase?: InterventionPhase;

  propertyUID: string;
  propertyName: string;
  propertyAddress: string;

  assignedProvider?: ServiceProvider;
  /** Provider UID as stored on the repair/maintenance — used to resolve the
   *  live provider from the providers store when `assignedProvider` is absent. */
  assignedTo?: string;
  /** Provider display name from the active visit — fallback label when no full
   *  provider record resolves (modern visit-based scheduling flow). */
  assignedProviderName?: string;
  assignedDate?: Date;

  scheduledDate?: Date;
  dueDate?: Date;
  estimatedDuration?: number;

  progress?: number;
  completedDate?: Date;

  attachments?: number;
  comments?: number;
  cost?: number;
  /** Quoted/estimated cost — shown until the final cost is confirmed. */
  estimatedCost?: number;
  /** Confirmed final cost — supersedes `estimatedCost` once set. */
  actualCost?: number;
  urgencyReason?: string;
  specialties?: string[];

  completionNotes?: string;
  rating?: number;
  costActual?: number;
  durationActual?: number;
  issues?: string[];
  recommendations?: string;

  createdAt: Date;
  updatedAt: Date;
}

export enum TaskStatus {
  PLANNED = 'planned',
  SCHEDULED = 'scheduled',
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

export interface PropertyOption {
  propertyUID: string;
  name: string;
  address: string;
  taskCount: number;
}
